import { styled } from '@mui/material';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import FileViewerControls from 'shell/components/FileViewerControls';
import Loading from 'shell/components/Loading';
import Stack from 'shell/components/Stack';
import { read, utils } from 'xlsx';

const HIGHLIGHT_BACKGROUND = 'sheet-highlight-bg';
const HIGHLIGHT_TOP = 'sheet-highlight-top';
const HIGHLIGHT_RIGHT = 'sheet-highlight-right';
const HIGHLIGHT_BOTTOM = 'sheet-highlight-bottom';
const HIGHLIGHT_LEFT = 'sheet-highlight-left';

const RootStack = styled(Stack)(({ theme }) => {
    return {
        position: 'relative',
        width: '100%',
        height: '100%',
        background: theme.palette.ensightful.shades[0],
    };
});

const TableStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        height: '100%',
        padding: theme.spacing(8),
        overflow: 'auto',
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '-5px',
        },
    };
});

const CustomTable = styled('table')(({ theme, zoom }) => {
    return {
        tableLayout: 'fixed',
        border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderCollapse: 'collapse',
        width: 'max-content',
        zoom,
        '& th, td': {
            border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
            minWidth: '50px',
            maxWidth: '300px',
            minHeight: '30px',
            height: '30px',
            maxHeight: '30px',
            padding: theme.spacing(1),
            fontSize: `${theme.typography.p2.fontSize['large']}rem`,
            fontWeight: theme.typography.p2.fontWeight.regular,
            textAlign: 'left',
            verticalAlign: 'top',
            [`&.${HIGHLIGHT_BACKGROUND}`]: {
                border: `1px solid ${theme.palette.ensightful.neutral[400]}`,
                background: theme.palette.ensightful.warning[300],
            },
            [`&.${HIGHLIGHT_TOP}`]: {
                borderTop: `2px solid ${theme.palette.ensightful.warning[700]}`,
            },
            [`&.${HIGHLIGHT_RIGHT}`]: {
                borderRight: `2px solid ${theme.palette.ensightful.warning[700]}`,
            },
            [`&.${HIGHLIGHT_BOTTOM}`]: {
                borderBottom: `2px solid ${theme.palette.ensightful.warning[700]}`,
            },
            [`&.${HIGHLIGHT_LEFT}`]: {
                borderLeft: `2px solid ${theme.palette.ensightful.warning[700]}`,
            },
        },
    };
});

const fetchFile = async (url) => {
    const response = await fetch(url);
    return await response.arrayBuffer();
};

const getClassNames = (classes) => {
    return classes.reduce((acc, cur) => `${acc} ${cur}`, '');
};

export default forwardRef(({ highlights, url }, ref) => {
    const [data, setData] = useState(null);
    const [scale, setScale] = useState(1);
    const [hidden, setHidden] = useState(false);

    const indexHighlights = useMemo(
        () => highlights.map(({ value }) => ({ row: value.row, col: value.col })),
        [highlights]
    );

    useEffect(() => {
        (async () => {
            const file = await fetchFile(url);
            const wb = read(file);
            const ws = wb.Sheets[wb.SheetNames[0]];
            const data = utils.sheet_to_json(ws, { blankrows: true, defval: '', header: 1, raw: false }).map((row) => {
                return row.map((item) => ({ classes: [], value: item }));
            });
            data.forEach((dataItem, r) => {
                dataItem.forEach((_, c) => {
                    if (indexHighlights.some(({ row, col }) => row === r && col === c)) {
                        data[r][c].classes.push(HIGHLIGHT_BACKGROUND);

                        if (!indexHighlights.some(({ row, col }) => row === r - 1 && col === c)) {
                            if (data[r - 1]?.[c]) {
                                data[r - 1][c].classes.push(HIGHLIGHT_BOTTOM);
                            } else {
                                data[r][c].classes.push(HIGHLIGHT_TOP);
                            }
                        }

                        if (!indexHighlights.some(({ row, col }) => row === r && col === c + 1)) {
                            if (data[r]?.[c + 1]) {
                                data[r][c + 1].classes.push(HIGHLIGHT_LEFT);
                            } else {
                                data[r][c].classes.push(HIGHLIGHT_RIGHT);
                            }
                        }

                        if (!indexHighlights.some(({ row, col }) => row === r + 1 && col === c)) {
                            if (data[r + 1]?.[c]) {
                                data[r + 1][c].classes.push(HIGHLIGHT_TOP);
                            } else {
                                data[r][c].classes.push(HIGHLIGHT_BOTTOM);
                            }
                        }

                        if (!indexHighlights.some(({ row, col }) => row === r && col === c - 1)) {
                            if (data[r]?.[c - 1]) {
                                data[r][c - 1].classes.push(HIGHLIGHT_RIGHT);
                            } else {
                                data[r][c].classes.push(HIGHLIGHT_LEFT);
                            }
                        }
                    }
                });
            });

            setData(data);
        })();
    }, [url, indexHighlights]);

    if (!data) return <Loading />;

    return (
        <RootStack ref={ref}>
            <TableStack>
                <CustomTable zoom={scale}>
                    <thead>
                        {data[0] ? (
                            <tr>
                                {data[0].map((item, index) => (
                                    <th key={index} className={getClassNames(item.classes)}>
                                        {item.value}
                                    </th>
                                ))}
                            </tr>
                        ) : null}
                    </thead>
                    <tbody>
                        {data.slice(1).length
                            ? data.slice(1).map((row, rowIndex) => {
                                  return (
                                      <tr key={rowIndex}>
                                          {row.map((item, itemIndex) => (
                                              <td key={itemIndex} className={getClassNames(item.classes)}>
                                                  {item.value}
                                              </td>
                                          ))}
                                      </tr>
                                  );
                              })
                            : null}
                    </tbody>
                </CustomTable>
            </TableStack>
            {data && (
                <FileViewerControls
                    currentPage={1}
                    numPages={1}
                    scale={scale}
                    setScale={setScale}
                    hidden={hidden}
                    setHidden={setHidden}
                />
            )}
        </RootStack>
    );
});
